/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Unified Media Gallery & Asset Manager Store (Task 10.4.6)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import { create } from 'zustand';

export type MediaAssetType = 'image' | 'video' | 'audio';
export type MediaSortOption = 'newest' | 'oldest' | 'size-desc' | 'credits-desc';

export interface MediaAsset {
  id: string;
  title: string;
  assetType: MediaAssetType;
  mimeType: string;
  url: string;
  thumbnailUrl: string;
  fileSizeBytes: number;
  width?: number;
  height?: number;
  durationSeconds?: number;
  bitrate?: string;
  generatorCode: string;
  generatorName: string;
  prompt?: string | null;
  negativePrompt?: string | null;
  creditsCost: number;
  createdAt: string;
  isFavorite: boolean;
  r2Key: string;
  aspectRatio?: '16:9' | '9:16' | '1:1' | '4:3' | '21:9';
  tags: string[];
}

export interface StorageMetrics {
  totalCount: number;
  totalSizeBytes: number;
  totalCredits: number;
  formattedSize: string;
}

export interface MediaGalleryState {
  assets: MediaAsset[];
  searchQuery: string;
  typeFilter: 'all' | MediaAssetType;
  modelFilter: string;
  sortOption: MediaSortOption;
  onlyFavorites: boolean;

  // Inspector & Preview
  activeAsset: MediaAsset | null;

  // Batch Selection
  isSelectionMode: boolean;
  selectedIds: string[];

  // Action methods
  setSearchQuery: (query: string) => void;
  setTypeFilter: (filter: 'all' | MediaAssetType) => void;
  setModelFilter: (model: string) => void;
  setSortOption: (sort: MediaSortOption) => void;
  setOnlyFavorites: (only: boolean) => void;
  setActiveAsset: (asset: MediaAsset | null) => void;

  toggleSelectionMode: () => void;
  toggleSelect: (id: string) => void;
  selectAll: () => void;
  deselectAll: () => void;

  toggleFavorite: (id: string) => void;
  renameAsset: (id: string, newTitle: string) => void;
  deleteAsset: (id: string) => void;
  batchDelete: (ids: string[]) => void;
  addAsset: (asset: MediaAsset) => void;

  // Computed Selectors
  getFilteredAssets: () => MediaAsset[];
  getStorageMetrics: () => StorageMetrics;
}

export function formatBytes(bytes: number, decimals = 1): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

export const SEED_MEDIA_ASSETS: MediaAsset[] = [
  {
    id: 'asset-img-001',
    title: 'Cybernetic Neon Tiger in Tokyo',
    assetType: 'image',
    mimeType: 'image/webp',
    url: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=1600&q=80',
    thumbnailUrl: 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 2450000, // 2.3 MB
    width: 1024,
    height: 1024,
    generatorCode: 'flux-pro',
    generatorName: 'Flux 1.1 Pro',
    prompt: 'A cybernetic neon tiger with glowing holographic stripes prowling through Tokyo neon night market, cinematic reflections, ultra sharp 8k',
    negativePrompt: 'blurry, low quality, distorted, extra limbs',
    creditsCost: 5,
    createdAt: '2026-10-25T06:30:00.000Z',
    isFavorite: true,
    r2Key: 'images/ws_prod/asset-img-001.webp',
    aspectRatio: '1:1',
    tags: ['cyberpunk', 'tiger', 'neon', 'flux'],
  },
  {
    id: 'asset-vid-002',
    title: 'Misty Vietnamese Mountain Pagoda Drone Shot',
    assetType: 'video',
    mimeType: 'video/mp4',
    url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1528127269322-539801943592?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 18400000, // 17.5 MB
    width: 1920,
    height: 1080,
    durationSeconds: 10,
    bitrate: '14.5 Mbps',
    generatorCode: 'kling-v1',
    generatorName: 'Kling AI 1.5',
    prompt: 'Cinematic drone flythrough of ancient Vietnamese pagodas on mountain ridge shrouded in morning clouds and golden sunrise light',
    negativePrompt: 'jerky movement, pixelated, jitter',
    creditsCost: 20,
    createdAt: '2026-10-25T05:15:00.000Z',
    isFavorite: true,
    r2Key: 'videos/ws_prod/asset-vid-002.mp4',
    aspectRatio: '16:9',
    tags: ['drone', 'vietnam', 'cinematic', 'kling'],
  },
  {
    id: 'asset-vid-003',
    title: 'Autonomous Hypercar Wet Asphalt Chase',
    assetType: 'video',
    mimeType: 'video/mp4',
    url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/WeAreGoingOnBullrun.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1617788138017-80ad40651399?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 9800000, // 9.3 MB
    width: 1920,
    height: 1080,
    durationSeconds: 5,
    bitrate: '15.2 Mbps',
    generatorCode: 'luma-dream-machine',
    generatorName: 'Luma Dream Machine',
    prompt: 'Futuristic autonomous electric hypercar racing on rain-soaked asphalt highway, camera tracking pan right, hyper-realistic reflections',
    creditsCost: 10,
    createdAt: '2026-10-24T22:40:00.000Z',
    isFavorite: false,
    r2Key: 'videos/ws_prod/asset-vid-003.mp4',
    aspectRatio: '16:9',
    tags: ['hypercar', 'chase', 'luma'],
  },
  {
    id: 'asset-avatar-004',
    title: 'Mai Linh Corporate Training Intro',
    assetType: 'video',
    mimeType: 'video/mp4',
    url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/TearsOfSteel.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 24600000, // 23.5 MB
    width: 1920,
    height: 1080,
    durationSeconds: 30,
    bitrate: '6.5 Mbps',
    generatorCode: 'synthesia',
    generatorName: 'Synthesia Studio v2',
    prompt: 'Presenter Mai Linh in modern office delivering comprehensive onboarding guide for software engineering teams',
    creditsCost: 30,
    createdAt: '2026-10-24T18:20:00.000Z',
    isFavorite: false,
    r2Key: 'videos/ws_prod/asset-avatar-004.mp4',
    aspectRatio: '16:9',
    tags: ['avatar', 'presenter', 'training', 'synthesia'],
  },
  {
    id: 'asset-img-005',
    title: 'Minimalist Coastal Ocean Villa',
    assetType: 'image',
    mimeType: 'image/png',
    url: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1600&q=80',
    thumbnailUrl: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 3250000, // 3.1 MB
    width: 1792,
    height: 1024,
    generatorCode: 'dalle-3',
    generatorName: 'OpenAI DALL-E 3',
    prompt: 'Ultra-luxurious minimalist brutalist villa perched on cliff overlooking ocean, warm sunset lighting, infinity pool, architectural digest photography',
    creditsCost: 4,
    createdAt: '2026-10-24T14:10:00.000Z',
    isFavorite: true,
    r2Key: 'images/ws_prod/asset-img-005.png',
    aspectRatio: '16:9',
    tags: ['architecture', 'villa', 'dalle3'],
  },
  {
    id: 'asset-aud-006',
    title: 'Product Launch Audio Voiceover (Mai)',
    assetType: 'audio',
    mimeType: 'audio/mpeg',
    url: 'https://actions.google.com/sounds/v1/weather/rain_heavy.ogg',
    thumbnailUrl: 'https://images.unsplash.com/photo-1478760329108-5c3ed9d495a0?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 1250000, // 1.2 MB
    durationSeconds: 42,
    bitrate: '320 kbps',
    generatorCode: 'elevenlabs',
    generatorName: 'ElevenLabs Studio',
    prompt: 'Chào mừng các bạn đến với Sophia Platform, nền tảng điện toán đám mây thế hệ mới trên Cloudflare Workers...',
    creditsCost: 2,
    createdAt: '2026-10-24T10:05:00.000Z',
    isFavorite: false,
    r2Key: 'audio/ws_prod/asset-aud-006.mp3',
    tags: ['voiceover', 'vietnamese', 'elevenlabs', 'audio'],
  },
  {
    id: 'asset-img-007',
    title: 'Cute Robotic Companion with Bonsai Garden',
    assetType: 'image',
    mimeType: 'image/webp',
    url: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=1600&q=80',
    thumbnailUrl: 'https://images.unsplash.com/photo-1485827404703-89b55fcc595e?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 1850000, // 1.8 MB
    width: 1024,
    height: 1024,
    generatorCode: 'sd-3.5',
    generatorName: 'Stable Diffusion 3.5 Large',
    prompt: 'Isometric 3D render of friendly white robotic companion watering miniature glowing bonsai tree in glass dome, soft studio lighting, octane render',
    creditsCost: 3,
    createdAt: '2026-10-23T20:30:00.000Z',
    isFavorite: false,
    r2Key: 'images/ws_prod/asset-img-007.webp',
    aspectRatio: '1:1',
    tags: ['robot', '3d', 'sd35'],
  },
  {
    id: 'asset-aud-008',
    title: 'Customer Onboarding Guidance Audio Clip',
    assetType: 'audio',
    mimeType: 'audio/mpeg',
    url: 'https://actions.google.com/sounds/v1/ambiences/daytime_forest_bonfire.ogg',
    thumbnailUrl: 'https://images.unsplash.com/photo-1511671782779-c97d3d27a1d4?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 860000, // 840 KB
    durationSeconds: 28,
    bitrate: '256 kbps',
    generatorCode: 'azure-tts',
    generatorName: 'Azure Neural Speech',
    prompt: 'Your workspace has been successfully created. Explore our tools from the navigation menu.',
    creditsCost: 2,
    createdAt: '2026-10-23T16:15:00.000Z',
    isFavorite: false,
    r2Key: 'audio/ws_prod/asset-aud-008.mp3',
    tags: ['audio', 'azure', 'tts', 'onboarding'],
  },
  {
    id: 'asset-vid-009',
    title: 'Blooming Bioluminescent Lotus Macro Shot',
    assetType: 'video',
    mimeType: 'video/mp4',
    url: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4',
    thumbnailUrl: 'https://images.unsplash.com/photo-1508615039623-a25605d2b022?auto=format&fit=crop&w=600&q=80',
    fileSizeBytes: 14200000, // 13.5 MB
    width: 1080,
    height: 1920,
    durationSeconds: 5,
    bitrate: '18.4 Mbps',
    generatorCode: 'runway-gen3',
    generatorName: 'Runway Gen-3 Alpha',
    prompt: 'Macro slow-motion shot of a bioluminescent lotus flower opening its petals, glowing pollen floating, deep purple and teal atmosphere',
    creditsCost: 10,
    createdAt: '2026-10-23T09:40:00.000Z',
    isFavorite: true,
    r2Key: 'videos/ws_prod/asset-vid-009.mp4',
    aspectRatio: '9:16',
    tags: ['lotus', 'macro', 'bioluminescence', 'runway'],
  },
];

export const useMediaGalleryStore = create<MediaGalleryState>((set, get) => ({
  assets: SEED_MEDIA_ASSETS,
  searchQuery: '',
  typeFilter: 'all',
  modelFilter: 'all',
  sortOption: 'newest',
  onlyFavorites: false,

  activeAsset: null,

  isSelectionMode: false,
  selectedIds: [],

  setSearchQuery: (query) => set({ searchQuery: query }),
  setTypeFilter: (filter) => set({ typeFilter: filter }),
  setModelFilter: (model) => set({ modelFilter: model }),
  setSortOption: (sort) => set({ sortOption: sort }),
  setOnlyFavorites: (only) => set({ onlyFavorites: only }),
  setActiveAsset: (asset) => set({ activeAsset: asset }),

  toggleSelectionMode: () =>
    set((state) => ({
      isSelectionMode: !state.isSelectionMode,
      selectedIds: [],
    })),

  toggleSelect: (id) =>
    set((state) => {
      const exists = state.selectedIds.includes(id);
      return {
        selectedIds: exists
          ? state.selectedIds.filter((item) => item !== id)
          : [...state.selectedIds, id],
      };
    }),

  selectAll: () => {
    const currentFiltered = get().getFilteredAssets();
    set({ selectedIds: currentFiltered.map((a) => a.id) });
  },

  deselectAll: () => set({ selectedIds: [] }),

  toggleFavorite: (id) =>
    set((state) => ({
      assets: state.assets.map((asset) =>
        asset.id === id ? { ...asset, isFavorite: !asset.isFavorite } : asset
      ),
      activeAsset:
        state.activeAsset && state.activeAsset.id === id
          ? { ...state.activeAsset, isFavorite: !state.activeAsset.isFavorite }
          : state.activeAsset,
    })),

  renameAsset: (id, newTitle) => {
    const trimmed = newTitle.trim();
    if (!trimmed) return;
    set((state) => ({
      assets: state.assets.map((asset) =>
        asset.id === id ? { ...asset, title: trimmed } : asset
      ),
      activeAsset:
        state.activeAsset && state.activeAsset.id === id
          ? { ...state.activeAsset, title: trimmed }
          : state.activeAsset,
    }));
  },

  deleteAsset: (id) =>
    set((state) => ({
      assets: state.assets.filter((asset) => asset.id !== id),
      selectedIds: state.selectedIds.filter((itemId) => itemId !== id),
      activeAsset: state.activeAsset?.id === id ? null : state.activeAsset,
    })),

  batchDelete: (ids) =>
    set((state) => ({
      assets: state.assets.filter((asset) => !ids.includes(asset.id)),
      selectedIds: [],
      isSelectionMode: false,
      activeAsset:
        state.activeAsset && ids.includes(state.activeAsset.id)
          ? null
          : state.activeAsset,
    })),

  addAsset: (asset) =>
    set((state) => ({
      assets: [asset, ...state.assets],
    })),

  getFilteredAssets: () => {
    const { assets, searchQuery, typeFilter, modelFilter, sortOption, onlyFavorites } = get();

    return assets
      .filter((asset) => {
        // 1. Search Query
        if (searchQuery.trim()) {
          const q = searchQuery.toLowerCase();
          const matchTitle = asset.title.toLowerCase().includes(q);
          const matchPrompt = asset.prompt?.toLowerCase().includes(q) ?? false;
          const matchModel = asset.generatorName.toLowerCase().includes(q);
          const matchTag = asset.tags.some((t) => t.toLowerCase().includes(q));
          if (!matchTitle && !matchPrompt && !matchModel && !matchTag) return false;
        }

        // 2. Type Filter
        if (typeFilter !== 'all' && asset.assetType !== typeFilter) {
          return false;
        }

        // 3. Model Filter
        if (modelFilter !== 'all') {
          if (!asset.generatorCode.toLowerCase().includes(modelFilter.toLowerCase())) {
            return false;
          }
        }

        // 4. Favorites Only
        if (onlyFavorites && !asset.isFavorite) {
          return false;
        }

        return true;
      })
      .sort((a, b) => {
        switch (sortOption) {
          case 'oldest':
            return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          case 'size-desc':
            return b.fileSizeBytes - a.fileSizeBytes;
          case 'credits-desc':
            return b.creditsCost - a.creditsCost;
          case 'newest':
          default:
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
        }
      });
  },

  getStorageMetrics: () => {
    const { assets } = get();
    const totalCount = assets.length;
    const totalSizeBytes = assets.reduce((sum, a) => sum + a.fileSizeBytes, 0);
    const totalCredits = assets.reduce((sum, a) => sum + a.creditsCost, 0);

    return {
      totalCount,
      totalSizeBytes,
      totalCredits,
      formattedSize: formatBytes(totalSizeBytes),
    };
  },
}));
